// src/api/axiosBoot.js
//
// Side-effect module: installs request/response interceptors on BOTH the
// default axios instance AND window.fetch so every component (including
// ones that call axios or fetch directly without going through
// services/http.js) automatically:
//
//   * sends the httpOnly `authToken` cookie on same-origin /api/* requests
//     (axios.defaults.withCredentials = true; fetch wrapper forces
//     `credentials: 'include'` for our /api/* calls)
//   * on 401, clears any stale localStorage token (left over from
//     pre-cookie-migration sessions) and redirects to /login.
//
// Phase 2b of the JWT-cookie migration removed the bearer-from-localStorage
// inject path on BOTH the axios interceptor and the fetch wrapper. New
// sessions never write `authToken` to localStorage; old sessions all
// expired with the 8h JWT TTL. The cookie is now the sole auth transport.
//
// Imported once from src/index.js BEFORE App is rendered. Other axios
// instances (e.g. createClient(tenant) in src/api/client.js) are
// unaffected — they install their own interceptors in that file.

import axios from 'axios';
import { isPublicPath } from '../utils/publicPaths';

// Send the auth cookie on every axios request (default is false, which
// would suppress cookies even on same-origin in some browsers / older XHR
// configurations). Set BEFORE the interceptors install so any request
// triggered during module init already inherits this default.
axios.defaults.withCredentials = true;

let _installed = false;

// URLs whose 401 responses must NOT trigger a redirect to /login. Covers two
// kinds of endpoints:
//   1. Auth flows (login/register/member-login). A failed login already shows
//      its own error UI — bouncing the user to /login would be a no-op loop.
//   2. Public/telemetry endpoints called from public pages (Download, Login,
//      etc.) where the user genuinely has no session yet. A 401 here is just
//      "we'd have logged this if you were signed in" — silently ignore.
function isLoginUrl(url) {
  const s = String(url || '');
  return /\/api\/auth\/(login|register)/.test(s)
    || /\/api\/members\/[^/]+\/member-login/.test(s)
    || /\/api\/members\/[^/]+\/member-set-password/.test(s)
    || /\/api\/members\/[^/]+\/member-request-reset/.test(s)
    || /\/api\/errors\/client(\b|\/|$)/.test(s);
}

// Public routes a signed-out visitor is *meant* to be able to load live in
// the shared utils/publicPaths.js module (also consumed by api/client.js and
// hooks/useSessionTimeout.js). A 401 from a background API call on one of
// these pages (e.g. a returning visitor whose 8h authToken cookie expired
// but whose localStorage `me` is still present, so the i18n provider fires
// GET /api/users/:id/language) must NOT bounce them to /login — that turns
// the marketing / legal / signup pages into an auth wall.

// Best-effort scrub of stale client-side auth state. The httpOnly authToken
// cookie is already dead (that's why we got a 401); this clears the
// localStorage mirror so the app renders as signed-out instead of
// re-firing authed calls on every subsequent page load.
function clearStaleAuthState() {
  try {
    ['authToken', 'sessionId', 'tenantid', 'tenantId', 'ownerid', 'userId',
     'username', 'role', 'me', 'user', 'lastActivity'].forEach(k => localStorage.removeItem(k));
  } catch { /* ignore */ }
  try { if (window.__ME__) delete window.__ME__; } catch { /* ignore */ }
}

function handle401Redirect() {
  if (typeof window === 'undefined') return;
  clearStaleAuthState();
  const onLogin = window.location.pathname.startsWith('/login');
  const inPopup = window.opener && window.opener !== window;
  const inFrame = window.top !== window.self;
  // On a public page, silently drop the stale session — never redirect.
  if (isPublicPath(window.location.pathname)) return;
  if (!onLogin && !inPopup && !inFrame) {
    window.location.assign('/login');
  }
}

// Several components build absolute API URLs from REACT_APP_BASE_URL (e.g.
// Integrations.jsx's `baseUrl`) instead of a relative /api/ path. That's a
// different origin than window.location.origin whenever the frontend and
// backend run on separate hosts/ports — always true in local dev (client on
// :3000, API on :5000) and possible in prod if they're ever split across
// subdomains. Those calls are still unambiguously "our API," so they must be
// recognized here too, or the cookie silently gets dropped (fetch's default
// credentials mode is 'same-origin') and every such call 401s.
function getConfiguredApiOrigin() {
  if (typeof window === 'undefined') return null;
  try {
    const base = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BASE_URL) || '';
    if (!base) return null;
    return new URL(base, window.location.origin).origin;
  } catch {
    return null;
  }
}
const CONFIGURED_API_ORIGIN = getConfiguredApiOrigin();

function isSameOriginApi(url) {
  if (typeof window === 'undefined') return false;
  const s = String(url || '');
  // Relative URLs starting with /api/ → same origin.
  if (s.startsWith('/api/')) return true;
  // Absolute URLs matching the current origin, or the configured API origin,
  // AND hitting /api/.
  try {
    const u = new URL(s, window.location.origin);
    if (!u.pathname.startsWith('/api/')) return false;
    return u.origin === window.location.origin || u.origin === CONFIGURED_API_ORIGIN;
  } catch {
    return false;
  }
}

export function installAxiosAuth() {
  if (_installed) return;
  _installed = true;

  // No request interceptor needed for auth — axios.defaults.withCredentials
  // above causes the browser to send the httpOnly authToken cookie on every
  // same-origin request. Each call site that needs other request shaping
  // (timeouts, custom headers) sets its own options.

  axios.interceptors.response.use(
    (resp) => resp,
    (err) => {
      const status = err?.response?.status;
      const url = err?.config?.url;
      if (status === 401 && !isLoginUrl(url)) handle401Redirect();
      return Promise.reject(err);
    }
  );

  installFetchAuth();
}

// --- window.fetch wrapper ------------------------------------------------
// Roughly half the codebase still uses bare fetch() for /api/* calls
// (scheduledPostsApi, inboxApi, analyticsApi, globalErrorHandler, several
// components). The wrapper forces credentials:'include' so the cookie
// flows even when a caller explicitly passed credentials:'omit', and
// routes 401s through handle401Redirect for consistency with axios.
function installFetchAuth() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  if (window.__onscreenFetchPatched__) return;
  window.__onscreenFetchPatched__ = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function patchedFetch(input, init) {
    let url;
    let request;
    if (typeof input === 'string') {
      url = input;
    } else if (input instanceof URL) {
      url = input.toString();
    } else if (input && typeof input === 'object' && 'url' in input) {
      request = input;
      url = input.url;
    }

    // Only adjust same-origin /api/* calls. External fetches (CDNs,
    // social media SDK pings, etc.) are left untouched.
    if (isSameOriginApi(url)) {
      // Ensure the auth cookie is sent. Fetch's default is 'same-origin'
      // which already includes cookies for same-origin requests, but
      // some callers explicitly pass credentials: 'omit', which we want
      // to override for our own /api/* calls.
      const callerCreds = (init && init.credentials)
        || (request && request.credentials);
      if (callerCreds !== 'include' && callerCreds !== 'same-origin') {
        init = { ...(init || {}), credentials: 'include' };
      }
    }

    const resp = await originalFetch(input, init);
    if (resp && resp.status === 401 && !isLoginUrl(url)) handle401Redirect();
    return resp;
  };
}

// Auto-install on import so the file is a single import line in index.js.
installAxiosAuth();
